import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import Button, { ButtonStyle } from '@components/buttons/Button/Button';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import { AircallService, IAircallCall } from '@services/aircall/AircallService';
import customerDetails from '@services/customer/CustomerDetails';
import LoadingSpinner from '@utils/loadingSpinner';
import React, { CSSProperties, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CallRow from './CallRow';

interface IBannerDetails {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
}

type FetchState = 'loading' | 'error' | 'loaded';

const bannerStyle: CSSProperties = {
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #C4C4C4',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px 20px',
    padding: '14px 24px',
};

const customerNameStyle: CSSProperties = {
    color: '#2C2A2D',
    fontSize: '16px',
    fontWeight: 600,
};

const emailStyle: CSSProperties = {
    color: '#6B7280',
    fontSize: '13px',
};

const crmIdPillStyle: CSSProperties = {
    backgroundColor: '#F4EBF5',
    border: '1px solid #E5D6E8',
    borderRadius: '12px',
    color: '#6F217B',
    fontFamily: 'monospace',
    fontSize: '12px',
    fontWeight: 600,
    padding: '2px 10px',
};

const errorNoteStyle: CSSProperties = {
    color: '#B91C1C',
    fontSize: '13px',
    fontStyle: 'italic',
};

const bannerSpinnerStyle: CSSProperties = {
    color: '#6B7280',
    fontSize: '13px',
};

const CustomerBanner = ({ zohoContactId, country }: { zohoContactId: string, country: string }) => {
    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const [state, setState] = useState<FetchState>('loading');
    const [details, setDetails] = useState<IBannerDetails | null>(null);

    // Inlined fetch (rather than useCustomerDetails / useServiceHook) so that a failure here
    // degrades gracefully — the shared hooks trigger a global error modal that redirects home,
    // which defeats the purpose of falling back to a CRM-ID-only banner.
    useEffect(() => {
        let cancelled = false;
        setState('loading');
        customerDetails(zohoContactId, country, { msalContext, isAuthenticated })
            .then(resp => {
                if (cancelled) return;
                const data = resp?.data;
                if (!data || resp?.status === 204) {
                    setState('error');
                    return;
                }
                setDetails({
                    firstName: data.first_name,
                    middleName: data.middle_name,
                    lastName: data.last_name,
                    email: data.email,
                });
                setState('loaded');
            })
            .catch(() => {
                if (cancelled) return;
                setState('error');
            });
        return () => { cancelled = true; };
    }, [zohoContactId, country]);

    if (state === 'loading') {
        return (
            <div style={bannerStyle}>
                <span style={crmIdPillStyle}>{zohoContactId}</span>
                <span style={bannerSpinnerStyle} data-testid="banner-loading-spinner">Loading details…</span>
            </div>
        );
    }

    if (state === 'error' || !details) {
        return (
            <div style={bannerStyle}>
                <span style={crmIdPillStyle}>{zohoContactId}</span>
                <span style={errorNoteStyle}>Could not load details</span>
            </div>
        );
    }

    const fullName = [details.firstName, details.middleName, details.lastName]
        .filter(part => part && part.trim())
        .join(' ');

    return (
        <div style={bannerStyle}>
            {fullName && <span style={customerNameStyle}>{fullName}</span>}
            {details.email && <span style={emailStyle}>{details.email}</span>}
            <span style={crmIdPillStyle}>{zohoContactId}</span>
        </div>
    );
};

const tableStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
};

const tableHeaderStyle: CSSProperties = {
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
    borderBottom: '1px solid #C4C4C4',
    color: '#6B7280',
    display: 'flex',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    padding: '10px 16px',
    textTransform: 'uppercase',
};

const columnStyle = (width: string): CSSProperties => ({
    flexShrink: 0,
    width,
});

const emptyStateStyle: CSSProperties = {
    color: '#6B7280',
    fontSize: '14px',
    fontStyle: 'italic',
    padding: '64px 16px',
    textAlign: 'center',
};

const errorBoxStyle: CSSProperties = {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '64px 16px',
    textAlign: 'center',
};

const errorMessageStyle: CSSProperties = {
    color: '#B91C1C',
    fontSize: '14px',
};

const retryButtonWrapperStyle: CSSProperties = {
    width: '140px',
};

const AircallArchiveView = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const zohoContactId = searchParams.get('zoho_contact_id');
    const country = searchParams.get('country');

    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const [retryCount, setRetryCount] = useState(0);
    const [expandedCallUuid, setExpandedCallUuid] = useState<string | null>(null);
    const [callsState, setCallsState] = useState<FetchState>('loading');
    const [calls, setCalls] = useState<IAircallCall[] | null>(null);

    useEffect(() => {
        if (!zohoContactId) {
            navigate('/');
        }
    }, [zohoContactId, navigate]);

    useEffect(() => {
        if (!zohoContactId) return;
        let cancelled = false;
        setCallsState('loading');
        AircallService.getCalls(zohoContactId, { msalContext, isAuthenticated })
            .then(result => {
                if (cancelled) return;
                setCalls(result);
                setCallsState('loaded');
            })
            .catch(() => {
                if (cancelled) return;
                setCallsState('error');
            });
        return () => { cancelled = true; };
    }, [zohoContactId, retryCount, msalContext, isAuthenticated]);

    const handleToggle = (callUuid: string) => {
        setExpandedCallUuid(prev => prev === callUuid ? null : callUuid);
    };

    const renderContent = () => {
        if (callsState === 'loading') {
            return <LoadingSpinner testId="calls-loading-spinner" />;
        }
        if (callsState === 'error') {
            return (
                <div style={errorBoxStyle}>
                    <span style={errorMessageStyle}>Unable to load call recordings.</span>
                    <div style={retryButtonWrapperStyle}>
                        <Button
                            label="Retry"
                            btnType={ButtonStyle.primary}
                            testId="retry-button"
                            onClickFunc={() => setRetryCount(c => c + 1)}
                        />
                    </div>
                </div>
            );
        }
        if (!calls || calls.length === 0) {
            return <p style={emptyStateStyle}>No call recordings found for this customer.</p>;
        }
        return (
            <div style={tableStyle}>
                <div style={tableHeaderStyle}>
                    <span style={columnStyle('12%')}>Date & Time</span>
                    <span style={columnStyle('7%')}>In/Out</span>
                    <span style={columnStyle('22%')}>Number</span>
                    <span style={columnStyle('18%')}>Agent</span>
                    <span style={columnStyle('8%')}>Duration</span>
                    <span style={columnStyle('10%')}>Recording</span>
                </div>
                {calls.map(call => (
                    <CallRow
                        key={call.call_uuid}
                        call={call}
                        isExpanded={expandedCallUuid === call.call_uuid}
                        onToggle={() => handleToggle(call.call_uuid)}
                        fetchAudioUrl={() => AircallService.getCallAudioUrl(
                            call.call_uuid,
                            { msalContext, isAuthenticated },
                        )}
                    />
                ))}
            </div>
        );
    };

    return (
        <div>
            <NavBar pageTitle="Aircall recordings" />
            {zohoContactId && country
                ? <CustomerBanner zohoContactId={zohoContactId} country={country} />
                : <div style={bannerStyle}><span style={crmIdPillStyle}>{zohoContactId}</span></div>
            }
            {renderContent()}
        </div>
    );
};

export default AircallArchiveView;
